import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { UserCacheService } from '@/lib/user-cache';
import { LabelCacheService } from '@/lib/labels';
import { resetAllStores } from '@/lib/reset-all-stores';

interface AuthContextType {
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({ user: null, loading: true });

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Track the previously-committed user id so we can detect a same-tab account switch (no page reload).
  const prevUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      prevUserIdRef.current = session?.user?.id ?? null;
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const nextUserId = session?.user?.id ?? null;
      // P0 cross-user leak fix: on logout OR an account switch (same tab, in-app navigate, no reload),
      // purge every in-memory user-scoped cache + store BEFORE committing the new identity — otherwise the
      // next user sees the prior user's role/label (caches) and catalog/drafts/master URLs (stores) until
      // they expire. Gate the "switch" branch on a non-null prevId so a fresh first load doesn't reset.
      const isSignOut = event === 'SIGNED_OUT';
      const isAccountSwitch =
        prevUserIdRef.current !== null && nextUserId !== prevUserIdRef.current;
      if (isSignOut || isAccountSwitch) {
        UserCacheService.clearCache();
        LabelCacheService.clearCache();
        resetAllStores();
      }
      prevUserIdRef.current = nextUserId;
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 